import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import { cn } from "@/lib/utils";
import useAutoOpen from "@/components/Playground/Drawer/Outputs/hooks/useAutoOpen";

const DrawerWidgetHeader = ({
  children,
  extraContent,
  count,
  isCollapsed,
  onToggle,
  className,
}: {
  children: React.ReactNode;
  extraContent?: React.ReactNode;
  count?: number;
  isCollapsed: boolean;
  onToggle?: () => void;
  className?: string;
}) => (
  <motion.button
    onClick={onToggle}
    className={cn("flex w-full items-center justify-between rounded-md px-3 py-2 ", className, {
      "hover:bg-gray-50": onToggle,
      "cursor-default": !onToggle,
    })}
    whileTap={onToggle ? { scale: 0.98 } : undefined}
  >
    <div className="flex w-full items-center gap-x-2">
      <div className="flex items-center text-sm font-medium text-gray-600">
        {!!onToggle && (
          <motion.svg
            animate={{ rotate: isCollapsed ? -90 : 0 }}
            transition={{ duration: 0.2 }}
            className="mr-1 h-3.5 w-3.5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </motion.svg>
        )}
        {children}
      </div>
      {count !== undefined && (
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600"
        >
          {count}
        </motion.div>
      )}
      {extraContent}
    </div>
  </motion.button>
);

interface DrawerWidgetProps {
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
  childrenClassName?: string;
  headerExtraContent?: React.ReactNode;
  name: string;
  count?: number;
  defaultCollapsed?: boolean;
  isCollapsible?: boolean;
  autoCollapse?: boolean;
  containerRef?: React.Ref<HTMLDivElement>;
  id?: string;
  /**
   * Pins the widget open and disables collapse + auto-collapse so the tour
   * spotlight target stays visible. Does not snap closed when released.
   */
  forceExpanded?: boolean;
  hideHeader?: boolean;
}

const DrawerWidget = ({
  children,
  className,
  contentClassName,
  childrenClassName,
  headerExtraContent,
  name,
  count = 0,
  defaultCollapsed = false,
  isCollapsible = true,
  autoCollapse = false,
  containerRef,
  id,
  forceExpanded = false,
  hideHeader = false,
}: DrawerWidgetProps) => {
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
  // Disable the auto-collapse resize observer while forced open so it does
  // not stomp on the override on small viewports.
  useAutoOpen(count, internalCollapsed, setInternalCollapsed, autoCollapse && !forceExpanded);

  const isCollapsed = forceExpanded ? false : internalCollapsed;

  const handleToggle = () => {
    if (isCollapsible && !forceExpanded) {
      setInternalCollapsed(!internalCollapsed);
    }
  };

  return (
    <div
      ref={containerRef}
      id={id}
      className={cn(
        "mx-3 rounded-lg border border-gray-100 bg-white p-2.5 shadow-sm",
        "[@media(max-height:600px)]:p-1.5",
        className,
      )}
    >
      {!hideHeader && (
        <DrawerWidgetHeader
          count={count}
          isCollapsed={isCollapsed}
          onToggle={isCollapsible ? handleToggle : undefined}
          extraContent={headerExtraContent}
          className="[@media(max-height:600px)]:py-1"
        >
          {name}
        </DrawerWidgetHeader>
      )}
      <AnimatePresence initial={false}>
        <motion.div
          key="content"
          initial="collapsed"
          animate={hideHeader || !isCollapsed ? "open" : "collapsed"}
          exit="collapsed"
          variants={{
            open: { opacity: 1, height: "auto" },
            collapsed: { opacity: 0, height: 0 },
          }}
          transition={{ duration: 0.2 }}
          style={{ overflow: "hidden" }}
          className={contentClassName}
        >
          <div
            className={cn(!hideHeader && "mt-2 [@media(max-height:600px)]:mt-1", childrenClassName)}
          >
            {children}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default DrawerWidget;
